import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { supabase } from '../../lib/supabase';
import { fetchPageContent, type PageSection } from '../../utils/pageContentStorage';
import { useSEO } from '@/hooks/useSEO';

const CATEGORIES = ['全部', 'iPhone', 'iPad', 'Mac', '其他'] as const;
type FilterType = typeof CATEGORIES[number];

interface NewsArticle {
  id: string;
  title: string;
  summary: string;
  image: string;
  published_at: string;
  category: string;
}

function getCaseCategory(title: string): Exclude<FilterType, '全部'> {
  if (title.toLowerCase().includes('iphone')) return 'iPhone';
  if (title.toLowerCase().includes('ipad')) return 'iPad';
  if (title.toLowerCase().includes('macbook') || title.toLowerCase().includes('mac')) return 'Mac';
  return '其他';
}

export default function CasesPage() {
  useSEO({
    title: '維修案例分享｜iPhone iPad MacBook 真實維修記錄',
    description: '兄弟維修真實維修案例記錄，包含 iPhone 螢幕破裂、電池老化、進水搶救，MacBook 主板維修、鍵盤更換等案例。技術透明，修前修後對比，讓您放心送修。',
    keywords: '手機維修案例,iPhone維修案例,MacBook維修案例,iPad維修案例,維修前後對比',
    canonical: '/cases',
  });

  const navigate = useNavigate();
  const [cases, setCases] = useState<NewsArticle[]>([]);
  const [filter, setFilter] = useState<FilterType>('全部');
  const [bannerData, setBannerData] = useState<PageSection | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPageContent().then(content => {
      setBannerData(content.find((s) => s.id === 'cases-banner') ?? null);
    });
    fetchCases();
  }, []);

  const fetchCases = async () => {
    try {
      const { data, error } = await supabase
        .from('news_articles')
        .select('id, title, summary, image, published_at, category')
        .eq('published', true)
        .eq('category', '維修案例')
        .order('published_at', { ascending: false });

      if (error) throw error;
      setCases(data || []);
    } catch (err) {
      console.error('Failed to fetch cases:', err);
    } finally {
      setLoading(false);
    }
  };

  const displayed =
    filter === '全部'
      ? cases
      : cases.filter((c) => getCaseCategory(c.title) === filter);

  const bannerTitle = bannerData?.title ?? '維修案例分享';
  const bannerSubtitle =
    bannerData?.subtitle ?? '真實維修案例記錄，展示我們的技術能力與維修成果';

  return (
    <>
      <Navbar />
      <main>
        {/* Hero Banner */}
        <section className="pt-32 pb-14 bg-gray-950">
          <div className="max-w-7xl mx-auto px-6 text-center">
            <div className="inline-flex items-center gap-2 text-orange-400 text-sm font-medium mb-4">
              <span className="w-8 h-px bg-orange-400" />
              案例分享
              <span className="w-8 h-px bg-orange-400" />
            </div>
            <h1 className="text-5xl font-bold text-white mb-4">{bannerTitle}</h1>
            <p className="text-gray-400 max-w-xl mx-auto">{bannerSubtitle}</p>
          </div>
        </section>

        {/* Stats bar */}
        <section className="bg-orange-500 py-6">
          <div className="max-w-7xl mx-auto px-6">
            <div className="flex flex-wrap justify-center gap-12">
              {[
                { num: `${cases.length}+`, label: '維修案例記錄' },
                { num: '98%', label: '客戶滿意度' },
                { num: '3個月', label: '維修保固' },
                { num: '177+', label: 'Instagram 貼文' },
              ].map((s) => (
                <div key={s.label} className="text-center text-white">
                  <div className="text-2xl font-bold">{s.num}</div>
                  <div className="text-orange-100 text-sm">{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6">
            {/* Category Filter */}
            <div className="flex flex-wrap gap-2 mb-10 justify-center">
              {CATEGORIES.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setFilter(cat)}
                  className={`px-5 py-2 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                    filter === cat
                      ? 'bg-orange-500 text-white'
                      : 'bg-white text-gray-600 border border-gray-200 hover:border-orange-300 hover:text-orange-500'
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>

            {/* Cases Grid */}
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : displayed.length === 0 ? (
              <div className="text-center py-20 text-gray-400">此類別目前暫無案例</div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {displayed.map((article) => (
                  <div
                    key={article.id}
                    onClick={() => navigate(`/news/${article.id}`)}
                    className="bg-white rounded-2xl overflow-hidden border border-gray-100 hover:-translate-y-1 transition-all duration-300 cursor-pointer group"
                  >
                    <div className="relative h-52 overflow-hidden">
                      <img
                        src={article.image}
                        alt={article.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
                      <div className="absolute top-3 right-3">
                        <span className="bg-orange-500 text-white text-xs font-semibold px-3 py-1 rounded-full">
                          {getCaseCategory(article.title)}
                        </span>
                      </div>
                      <div className="absolute bottom-3 left-4 text-white text-xs font-medium">{article.published_at}</div>
                    </div>
                    <div className="p-6">
                      <div className="flex items-center gap-2 mb-3">
                        <div className="w-5 h-5 flex items-center justify-center bg-orange-50 rounded">
                          <i className="ri-tools-line text-orange-500 text-xs" />
                        </div>
                        <span className="text-orange-500 text-xs font-medium">維修案例</span>
                      </div>
                      <h2 className="font-bold text-gray-900 text-base leading-snug mb-2 line-clamp-2">
                        {article.title}
                      </h2>
                      <p className="text-gray-500 text-sm leading-relaxed line-clamp-3 mb-4">
                        {article.summary}
                      </p>
                      <div className="flex items-center gap-1 text-orange-500 text-sm font-medium">
                        查看詳情
                        <div className="w-4 h-4 flex items-center justify-center">
                          <i className="ri-arrow-right-line text-sm" />
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Instagram Follow Section */}
            <div className="mt-16 rounded-3xl overflow-hidden relative">
              <img
                src="https://ollumiosiejdxqaygglt.supabase.co/storage/v1/object/public/news-images/site-images/igbanner01.jpg"
                alt="兄弟維修 Instagram"
                className="w-full h-64 object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-black/75 via-black/50 to-black/30 flex items-center">
                <div className="px-10 max-w-xl">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-9 h-9 flex items-center justify-center bg-gradient-to-tr from-yellow-400 via-orange-500 to-pink-500 rounded-xl">
                      <i className="ri-instagram-line text-white text-lg" />
                    </div>
                    <span className="text-orange-300 text-sm font-semibold tracking-wide">@brotherfix_1990</span>
                  </div>
                  <h2 className="text-white text-2xl font-bold mb-2">更多維修案例在 Instagram</h2>
                  <p className="text-gray-300 text-sm mb-6 leading-relaxed">
                    177 篇真實維修記錄，iPhone、iPad、MacBook 各式案例每日更新，追蹤我們掌握最新維修資訊！
                  </p>
                  <a
                    href="https://www.instagram.com/brotherfix_1990/"
                    target="_blank"
                    rel="nofollow noopener noreferrer"
                    className="inline-flex items-center gap-2 bg-gradient-to-r from-orange-500 to-pink-500 hover:from-orange-600 hover:to-pink-600 text-white font-semibold px-6 py-3 rounded-full transition-all cursor-pointer whitespace-nowrap"
                  >
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-instagram-line text-base" />
                    </div>
                    追蹤 Instagram
                  </a>
                </div>
              </div>
            </div>

            {/* CTA */}
            <div className="mt-8 bg-white rounded-2xl border border-gray-100 p-8 text-center">
              <h2 className="text-xl font-bold text-gray-900 mb-2">您的裝置也需要維修嗎？</h2>
              <p className="text-gray-500 text-sm mb-6">免費診斷，透明報價，多數當日完工</p>
              <div className="flex flex-wrap justify-center gap-4">
                <button
                  onClick={() => navigate('/pricing')}
                  className="px-6 py-3 bg-orange-500 hover:bg-orange-600 text-white font-semibold rounded-full cursor-pointer whitespace-nowrap transition-colors"
                >
                  查詢維修報價
                </button>
                <button
                  onClick={() => navigate('/contact')}
                  className="px-6 py-3 border-2 border-gray-900 hover:bg-gray-900 hover:text-white text-gray-900 font-semibold rounded-full cursor-pointer whitespace-nowrap transition-colors"
                >
                  聯絡我們
                </button>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}